import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { TreePine, Sparkles, Heart, Gift, ArrowRight, Truck, Leaf, Clock, CheckCircle2, MapPin } from "lucide-react";
import { fetchProducts } from "../lib/api";
import { findOccasion, FESTIVE_OCCASIONS, DELIVERY_AREAS } from "../data/seoContent";
import ProductCard from "../components/ProductCard";
import FaqSection from "../components/FaqSection";
import Seo from "../components/Seo";

const BASE_URL = "https://globaltreats.in";
const ICONS = { tree: TreePine, sparkles: Sparkles, heart: Heart, gift: Gift };

const OccasionPage = () => {
  const { slug } = useParams();
  const occ = findOccasion(slug);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    if (!occ) return;
    fetchProducts(occ.category).then((list) => {
      const src = list && list.length ? list : [];
      setProducts(src.slice(0, 4));
    }).catch(() => setProducts([]));
  }, [slug, occ]);

  if (!occ) return <Navigate to="/collections/all" replace />;

  const Icon = ICONS[occ.icon] || Gift;
  const otherOccasions = FESTIVE_OCCASIONS.filter((o) => o.slug !== slug);
  const someAreas = DELIVERY_AREAS.slice(0, 8);

  return (
    <div>
      <Seo
        path={`/occasions/${occ.slug}`}
        title={occ.title.replace(" | Globaltreats", "")}
        description={occ.description}
        image={occ.image?.startsWith("http") ? occ.image : `${BASE_URL}${occ.image || "/assets/og-banner.jpg"}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: `${occ.name} Delivery`,
          provider: { "@type": "Bakery", name: "Globaltreats", telephone: "+91-92649-71235", url: BASE_URL },
          areaServed: [{ "@type": "City", name: "Noida" }, { "@type": "City", name: "Greater Noida" }],
          url: `${BASE_URL}/occasions/${occ.slug}`,
          description: occ.description,
        }}
      />

      {/* Hero */}
      <section style={{ background: `linear-gradient(135deg, ${occ.accentSoft}, #FCEFE3)` }}>
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-16 text-center">
          <span className="inline-flex items-center gap-2 bg-white/70 text-sm px-4 py-1.5 rounded-full mb-5" style={{ color: occ.accent }}>
            <Icon size={15} /> {occ.badge}
          </span>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-[#3A2417] leading-tight">{occ.h1}</h1>
          <p className="text-[#6B4226]/80 text-lg mt-5 max-w-2xl mx-auto">{occ.blurb}</p>
          <div className="flex flex-wrap gap-3 justify-center mt-8">
            <Link to={`/collections/${occ.category}`} className="inline-flex items-center gap-2 h-12 px-7 rounded-full text-white font-medium transition-transform hover:-translate-y-0.5" style={{ backgroundColor: occ.accent }} data-testid="occasion-order-btn">
              Shop {occ.name} <ArrowRight size={18} />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-white text-[#6B4226] font-medium hover:bg-[#FCEFE3] transition-colors">Request a Custom Cake</Link>
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-14">
        <div className="grid sm:grid-cols-3 gap-6">
          {[
            { icon: Truck, t: "Next-day delivery", s: "Order ahead and we'll deliver fresh across Noida & Greater Noida on your chosen date." },
            { icon: Leaf, t: "Eggless options", s: "Every festive cake is available in a 100% eggless version at no extra cost." },
            { icon: Clock, t: "Baked fresh to order", s: "Nothing pre-made — your celebration cake is baked close to your delivery date." },
          ].map((f, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 bakery-shadow">
              <div className="w-12 h-12 rounded-full grid place-items-center mb-4" style={{ backgroundColor: occ.accentSoft }}><f.icon size={22} style={{ color: occ.accent }} /></div>
              <h3 className="font-display text-lg font-semibold text-[#3A2417]">{f.t}</h3>
              <p className="text-sm text-[#6B4226] mt-1.5">{f.s}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured products */}
      {products.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 pb-6">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-[#3A2417] mb-6">Popular {occ.name.toLowerCase()}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
          <div className="text-center mt-8">
            <Link to={`/collections/${occ.category}`} className="inline-flex items-center gap-2 text-sm font-medium" style={{ color: occ.accent }}>
              View all {occ.name.toLowerCase()} <ArrowRight size={15} />
            </Link>
          </div>
        </section>
      )}

      {/* Delivery note */}
      <section className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <div className="rounded-2xl p-6 flex items-start gap-3 bg-[#EAF3E4]">
          <CheckCircle2 size={22} className="text-[#6BA84F] mt-0.5 shrink-0" />
          <p className="text-[#3f6b2e] text-sm">
            <strong>We deliver {occ.name.toLowerCase()} across Noida & Greater Noida.</strong> Enter your pincode at checkout to confirm your area and pick your delivery date. Free delivery on orders above ₹999.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <FaqSection title={`${occ.name} — FAQs`} subtitle={`Common questions about ordering ${occ.name.toLowerCase()} for delivery in Noida & Greater Noida.`} />

      {/* Cross links */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 pb-16 space-y-8">
        <div>
          <h2 className="font-display text-xl font-semibold text-[#3A2417] mb-4">More seasonal specials</h2>
          <div className="flex flex-wrap gap-2.5">
            {otherOccasions.map((o) => {
              const OIcon = ICONS[o.icon] || Gift;
              return (
                <Link key={o.slug} to={`/occasions/${o.slug}`} className="inline-flex items-center gap-1.5 bg-white border border-[#EADBCB] rounded-full px-4 py-2 text-sm text-[#6B4226] hover:border-[#D46A8A] hover:text-[#D46A8A] transition-colors" data-testid={`occasion-link-${o.slug}`}>
                  <OIcon size={14} /> {o.name}
                </Link>
              );
            })}
          </div>
        </div>
        <div>
          <h2 className="font-display text-xl font-semibold text-[#3A2417] mb-4">Delivering to</h2>
          <div className="flex flex-wrap gap-2.5">
            {someAreas.map((a) => (
              <Link key={a.slug} to={`/cake-delivery/${a.slug}`} className="inline-flex items-center gap-1.5 bg-white border border-[#EADBCB] rounded-full px-4 py-2 text-sm text-[#6B4226] hover:border-[#D46A8A] hover:text-[#D46A8A] transition-colors">
                <MapPin size={13} /> {a.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default OccasionPage;
